import { useAppDispatch } from "@config/hooks";
import { TLoginReq } from "@models/login";
import { authActions } from "@redux/slices/authSlice";
import { Form, InputRef } from "antd";
import { useEffect, useRef, useState } from "react";
import { ValidateErrorEntity } from "types";
import { LOGIN_CONSTANT, REGEX_ID, REGEX_PASSWORD } from "../constant";
import { useNavigate } from "react-router-dom";
import { adminPaths } from "routers/routes";

const { MESSAGE, NAME } = LOGIN_CONSTANT;

export default function useLoginPage() {
  const [messageError, setMessageError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // Viền đỏ [1][2] chỉ khi auth fail (MSG-PC001-E01) — Screen Spec item [1][2] error state.
  const [isAuthError, setIsAuthError] = useState(false);

  const [form] = Form.useForm();
  const idWatch = Form.useWatch(NAME.USERNAME, form);
  const passwordWatch = Form.useWatch(NAME.PASSWORD, form);

  const navigate = useNavigate();

  const idRef = useRef<InputRef>(null);

  const dispatch = useAppDispatch();

  const handleLoginError = (code?: string) => {
    idRef.current?.focus();
    // HTTP 400 auth fail → saga truyền errorCode BE (WrongPassword / NotExistUser / DeactiveUser)
    //   → MSG-PC001-E01 inline [5] + viền đỏ [1][2].
    // Network / timeout / 5xx → saga truyền "" → MSG-PC001-E02 inline [5] (KHÔNG toast).
    if (code) {
      setIsAuthError(true);
      setMessageError(MESSAGE.WRONG_INFO);
      return;
    }
    setMessageError(MESSAGE.NETWORK_ERROR);
  };

  const handleSubmitFormSuccess = (values: TLoginReq) => {
    if (isLoading) {
      return;
    }
    setMessageError("");
    setIsAuthError(false);
    // ID: leading/trailing whitespace auto-trimmed (Screen Spec [1]); password giữ nguyên.
    const username = values.username?.trim() ?? "";
    const password = values.password ?? "";

    // Check validate format ID & Password (Screen Spec [1][2]); lỗi format → MSG-PC001-E01
    if (!REGEX_ID.test(username) || !REGEX_PASSWORD.test(password)) {
      setMessageError(MESSAGE.WRONG_INFO);
      idRef.current?.focus();
      return;
    }

    // If pass, call api login
    setIsLoading(true);
    dispatch(
      authActions.login({
        data: { username, password },
        // VAMS_PC_002 (メニュー) chưa dựng; màn メニュー tạm chứa PC004 → điều hướng thẳng
        // tới route PC004 `/fixedassetledger`. Khi PC002 build thật → đổi lại đích.
        onSuccess: () => navigate(adminPaths.home, { replace: true }),
        onError: handleLoginError,
        onFinal: () => {
          setIsLoading(false);
        },
      })
    );
  };

  const handleSubmitFormFail = ({
    errorFields,
  }: ValidateErrorEntity<TLoginReq>) => {
    const firstFieldErrorName = errorFields[0].name;
    form.getFieldInstance(firstFieldErrorName)?.focus();
  };

  useEffect(() => {
    idRef.current?.focus();
  }, []);

  return {
    state: {
      messageError,
      isLoading,
      isAuthError,
      form,
      idWatch,
      passwordWatch,
      idRef,
    },
    handler: {
      handleSubmitFormFail,
      handleSubmitFormSuccess,
    },
  };
}
